import { Activity } from 'lucide-react';
import { lazy } from 'react';
import { msg } from '@/lib/i18n';
import { registerInvalidation } from '@/lib/realtime';
import { registerEntityHref } from '@/ui';
import { registerEntityTab } from '../entityTabs';
import { defineModule } from '../types';
import { activityKeys } from './api';
import { activityRoute, compareRoute, eventRoute, eventsAliasRoute, eventsRoute, tripRoute, tripsAliasRoute, tripsRoute } from './routes';

/**
 * Activity (server module `telemetry`): the continuous timeline across the fleet, trips as one
 * lens on it, and the events found in the data (ADR-0006). Video lives in the media module.
 */
export const activityModule = defineModule({
  key: 'activity',
  routes: [activityRoute, tripsRoute, compareRoute, tripRoute, eventsRoute, tripsAliasRoute, eventsAliasRoute, eventRoute],
  nav: [
    {
      key: 'activity',
      label: msg('Activity'),
      description: msg('What happened?'),
      icon: Activity,
      to: '/activity',
      section: 'operate',
      order: 20,
      permission: 'trips.trip.read',
      module: 'telemetry',
      match: ['/activity', '/trips', '/events'],
      children: [
        { key: 'timeline', label: msg('Timeline'), to: '/activity', match: ['/activity'] },
        { key: 'trips', label: msg('Trips'), to: '/activity/trips', match: ['/activity/trips', '/trips'] },
        { key: 'events', label: msg('Events'), to: '/activity/events', match: ['/activity/events', '/events'] },
        { key: 'video', label: msg('Video'), to: '/activity/video', permission: 'media.video.read', match: ['/activity/video'] },
      ],
    },
  ],
  setup() {
    // Entity tabs (IA §3). These replace fleet's "Activity" placeholders (same key, registered later).
    registerEntityTab('vehicle', {
      key: 'activity',
      label: msg('Activity'),
      order: 20,
      permission: 'trips.trip.read',
      module: 'telemetry',
      component: lazy(() => import('./tabs/VehicleActivityTab')),
    });
    registerEntityTab('vehicle', {
      key: 'trips',
      label: msg('Trips'),
      order: 25,
      permission: 'trips.trip.read',
      module: 'telemetry',
      component: lazy(() => import('./tabs/VehicleTripsTab')),
    });
    registerEntityTab('driver', {
      key: 'activity',
      label: msg('Activity'),
      order: 20,
      permission: 'trips.trip.read',
      module: 'telemetry',
      component: lazy(() => import('./tabs/DriverActivityTab')),
    });
    registerEntityTab('driver', {
      key: 'trips',
      label: msg('Trips'),
      order: 25,
      permission: 'trips.trip.read',
      module: 'telemetry',
      component: lazy(() => import('./tabs/DriverTripsTab')),
    });
    // The trip page is extensible too: media adds "Video".
    registerEntityTab('trip', { key: 'overview', label: msg('Trip'), order: 10, component: lazy(() => import('./tabs/TripOverviewTab')) });

    registerEntityHref('trip', (id) => `/trips/${id}`);
    registerEntityHref('detection', (id) => `/events/${id}`);

    // Realtime: processed hours and reviews make the timeline, trips and events stale.
    registerInvalidation('vehicle', (n) =>
      n.type === 'telemetry.hour.processed' || n.type.startsWith('trips.')
        ? [activityKeys.all]
        : n.type.startsWith('media.')
          ? [
              ['activity', 'lanes'],
              ['activity', 'timeline'],
              ['activity', 'detections'],
            ]
          : [],
    );
  },
});
